/**
 * Wire time: the one place an instant becomes text, and text becomes an
 * instant, anywhere Bower crosses a process boundary (HTTP, MCP, sockets,
 * queues, exports).
 *
 * The contract, for every server and every client:
 *
 * - The database stores instants (`timestamptz`), so a stored value means the
 *   same moment whatever zone the server, the database or the session runs in.
 *   (Columns still listed in `scripts/wire-time-baseline.json` are not yet
 *   converted; until they are, the UTC session pin is what keeps them right.)
 * - The wire carries ISO-8601 in UTC with a `Z` suffix and millisecond
 *   precision (`2026-10-17T00:00:00.123Z`). That is what `toWireTime` emits and
 *   what `wireTimestampSchema` accepts.
 * - Clients localize. A browser, the mobile app or an MCP client renders the
 *   instant in its viewer's zone and labels the zone; the server never renders
 *   a time for a person.
 *
 * Input is slightly wider than output: a caller may send an instant with an
 * explicit offset (`+10:00`), which is just as unambiguous. What is rejected
 * everywhere is a time with NO zone (`2026-10-17T10:00:00`): `new Date()` reads
 * that in the parsing process's own zone, so the same request means a
 * different instant on a server in Iowa and one in Sydney.
 *
 * The one deliberate exception is a camera's wall clock (EXIF
 * `DateTimeOriginal`, a phone's `capturedAt`), which genuinely has no zone.
 * Those fields use `naiveWallClockSchema`, which names the ambiguity, and are
 * never compared with instants as if they were one.
 */
import { z } from 'zod';
/** Output contract: ISO-8601, UTC, `Z` suffix. Response shapes use this. */
export const wireTimestampSchema = z.iso.datetime();
/**
 * Input contract: an ISO-8601 instant with `Z` or an explicit `±hh:mm` offset.
 * An offset-less or date-only string is rejected rather than guessed at.
 */
export const instantInputSchema = z.iso.datetime({ offset: true });
/** `instantInputSchema`, parsed to a `Date`. For query and body validation. */
export const instantInputDateSchema = instantInputSchema.transform((value) => new Date(value));
/**
 * A wall-clock reading whose zone is unknown (a camera's EXIF clock). Accepts
 * offset-less input on purpose; the field's own docs must say so, and the value
 * must not be treated as an instant.
 */
export const naiveWallClockSchema = z.iso.datetime({ offset: true, local: true });
/** Serialize an instant for the wire. Throws on an invalid `Date`. */
export function toWireTime(date) {
    if (Number.isNaN(date.getTime())) {
        throw new RangeError('toWireTime: invalid Date');
    }
    return date.toISOString();
}
/**
 * The UTC calendar date of an instant, `YYYY-MM-DD`. Only for a value that is
 * explicitly a UTC date (a billing period boundary, a BigQuery partition); a
 * reader-facing date belongs to the client, in the reader's zone.
 */
export function toUtcDateString(date) {
    return toWireTime(date).slice(0, 10);
}
/**
 * Parse a wire timestamp into a `Date`. Accepts `Z` or an explicit offset;
 * throws on anything else, including an offset-less time, so a value with no
 * zone fails loudly instead of being read in this process's zone.
 */
export function parseWireTime(value) {
    if (!instantInputSchema.safeParse(value).success) {
        throw new RangeError('parseWireTime: expected an ISO-8601 instant with Z or an offset');
    }
    return new Date(value);
}
const ZONE_DESIGNATOR = /(?:Z|[+-]\d\d|[+-]\d\d:?\d\d)$/i;
/**
 * The lenient parse, for a model-facing filter that has always accepted a bare
 * date (`2024-01-31`) or a zone-less time (`2024-01-31T09:00`) because Bower's
 * own agent sends them. A zone-less value is read as UTC, explicitly, never in
 * this process's zone, so the same call means the same instant on every
 * server. Throws on anything `Date` cannot read.
 */
export function parseInstantReadingNaiveAsUtc(value) {
    const timeStart = value.search(/T/i);
    const naiveTime = timeStart >= 0 && !ZONE_DESIGNATOR.test(value.slice(timeStart));
    const parsed = new Date(naiveTime ? `${value}Z` : value);
    if (Number.isNaN(parsed.getTime())) {
        throw new RangeError('parseInstantReadingNaiveAsUtc: not an ISO-8601 date or date-time');
    }
    return parsed;
}
//# sourceMappingURL=wire-time.js.map